import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DeviceContext } from "../context/deviceContext";
import { AuctionSearchAndFilterContext } from "../context/auctionFilterContext.js";
import { getData } from "../fetchRoutes/getData";
import PropertyImageHandler from "../utility/imageHandler";
import PriceProp from "../props/priceProp.js";
import PropertyAddressProp from "../props/addressProp.js";
import PropertyDescriptionProp from "../props/descProp.js";
import CalculateAuctionTime from "../utility/calculateAuctionTime.js";
import AuctionSearchAndFilter from "../searchandfilter/auctionSearchandfilter.js";

export default function AuctionContainer() {

    const auctionsApiUrl = process.env.REACT_APP_GET_AUCTIONS;
    const navigate = useNavigate();

    const { isMobile } = useContext(DeviceContext);
    const { handleFilteration } = useContext(AuctionSearchAndFilterContext);

    const [auctions, setAuctions] = useState([]);
    const [visibleAuctions, setVisibleAuctions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isFetched, setIsFetched] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 4; // Load 4 auctions at a time

    const loaderRef = useRef(null);
    const hasFiltered = useRef(false);

    // Fetch Auctions
    useEffect(() => {
        const fetchAuctions = async () => {
            if (!auctionsApiUrl) return;
            setLoading(true);
            try {
                const response = await getData(auctionsApiUrl);
                if (response.status === "Success") {
                    const openAuctions = response.data.filter(auction => auction.status === "open");
                    setAuctions(openAuctions);
                }
            } catch (err) {
                console.error("Error fetching auctions:", err);
            } finally {
                setLoading(false);
                setIsFetched(true);
            }
        };

        if (!isFetched) {  
            fetchAuctions();
        }
    }, [auctionsApiUrl, isFetched]);

    // Apply filters
    useEffect(() => {
        if (auctions.length > 0 && !hasFiltered.current) {
            handleFilteration(setAuctions);
            hasFiltered.current = true;
        }
    }, [auctions, handleFilteration]);

    // Load more auctions
    const loadMoreAuctions = useCallback(() => {
        const nextPage = currentPage + 1;
        const startIndex = (nextPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;

        const nextAuctions = auctions.slice(startIndex, endIndex);

        if (nextAuctions.length > 0) {
            setVisibleAuctions((prev) => [...prev, ...nextAuctions]);
            setCurrentPage(nextPage);
        }
    }, [currentPage, auctions, pageSize]);

    // Initialize with the first batch of auctions
    useEffect(() => {
        if (auctions.length > 0) {
            setVisibleAuctions(auctions.slice(0, pageSize));
        }
    }, [auctions, pageSize]);

    // Intersection Observer for lazy loading
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMoreAuctions();
                }
            },
            { threshold: 1.0 }
        );

        if (loaderRef.current) {
            observer.observe(loaderRef.current);
        }

        return () => {
            if (loaderRef.current) {
                // eslint-disable-next-line
                observer.unobserve(loaderRef.current);
            }
        };
    }, [loadMoreAuctions]);


    // Navigate to property details
    const handleCardClick = (property) => {
        navigate(`/properties/property/${property._id}`);
    };

    return (
        <main className="auctionsPage">
            {/* Search and Filter Component */}
            <AuctionSearchAndFilter 
                setFilteredProperties={setVisibleAuctions}
                properties={auctions}
            />

            <div className="properties">
                {loading ? (
                    <div className="loaderContainer">
                        <div className="loader"></div>
                        <p className="loadingText">Loading...</p>
                    </div>
                ) : visibleAuctions.length > 0 ? (
                    visibleAuctions.map((auction, index) => (
                        <div className="propertyCards auctionCard" key={index}>
                            <PropertyImageHandler property={auction.property} />
                            
                            <div className="details">
                                {/* Property Price */}
                                <PriceProp
                                    mainClassName="price"
                                    spanClassname1="propertyPrice"
                                    spanClassname2="favorite"
                                    property={auction.property}
                                />

                                {/* Room and Address */}
                                <PropertyAddressProp
                                    mainClassName="roomAddress"
                                    roomClassName="houseRoomsDetails"
                                    addressClassName="propertyAddress"
                                    property={auction.property}
                                />

                                {/* Description */}
                                <PropertyDescriptionProp
                                    mainClassName="description"
                                    textClassName="propertyDescription"
                                    property={auction.property}
                                    isMobile={isMobile}
                                />

                                {/* Auction Timer */}
                                <div className="auctionTimerAndReservePrice">
                                    <CalculateAuctionTime
                                        property={auction.property}
                                        auctionStartTime={auction.startTime}
                                        auctionEndTime={auction.endTime}
                                        title={auction.property.title}
                                        description={auction.property.description}
                                        location={auction.property.address}
                                        isAuction={true}
                                    />

                                    {/* Reserve Price */}
                                    <div className="reservePrice" onClick={() => handleCardClick(auction.property)}>
                                        <div className="reserveDetail">
                                            <span className="reserve">Reserve Price:</span>
                                            <span className="reserveAmount"> £{(auction.reservePrice).toLocaleString()}</span>
                                        </div>
                                    </div>

                                    {/* Total Bids */}
                                    <div className="totalBids" onClick={() => handleCardClick(auction.property)}>
                                        <span className="bid">
                                            {auction.bids?.length > 0 ? (
                                                <span>Total Bids: {auction.bids.length}</span>
                                            ) : (
                                                <span>Be the first one to place the bid!</span>
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <p>No open auctions available.</p>
                )}
            </div>

            {/* Loader element to trigger lazy loading */}
            <div ref={loaderRef} style={{ height: "50px", margin: "20px 0" }}>
                {loading && <p>Loading more auctions...</p>}
            </div>
        </main>
    );
}
